import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { formatPhoneInput } from "@/lib/phone";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";

const ITEM_OPTIONS = ["Regular Medication", "Controlled Medication", "Refrigerated", "Other"];

// Restricted-ZIP list — sourced from `GET /api/admin/restricted-zips` so it
// stays in sync with whatever a Super Admin has configured on the Audit Log
// tab. The set is loaded once when the form mounts; any mid-session changes
// on another tab surface on the next mount.
// Weekdays at/after 2:00 PM America/New_York, independent of browser timezone.
const sameDayCutoffPassed = () => {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", weekday: "short", hour: "numeric", hourCycle: "h23" })
      .formatToParts(new Date()).map((p) => [p.type, p.value])
  );
  return !["Sat", "Sun"].includes(parts.weekday) && Number(parts.hour) >= 14;
};
const CUTOFF_MSG = "Same Day cutoff time is 2:00 PM Eastern Time. Orders placed after 2:00 PM are automatically scheduled as Next Day delivery.";

const extractZip = (addr) => {
  const m = String(addr || "").match(/\b(\d{5})(?:-\d{4})?\b/g);
  return m && m.length ? m[m.length - 1] : null;
};

export default function OrderForm({ onCreated, onCancel, defaultPharmacyId, pharmacies = [], allowPharmacySelect = true, initialAddress = "", initialDeliverType = "same_day", initialNotes = "", initialPatientName = "", initialPatientPhone = "", initialPriority = "normal" }) {
  const { user } = useAuth();
  const canSetCopay = user?.role === "super_admin" || user?.role === "pharmacy_staff";
  const isStaff = user?.role === "pharmacy_staff";
  const [cutoffActive, setCutoffActive] = useState(() => isStaff && sameDayCutoffPassed());
  const fixType = (t) => (t === "same_day" && isStaff && sameDayCutoffPassed() ? "next_day" : t);
  const [copayEnabled, setCopayEnabled] = useState(false);
  // Explicit "signature required" toggle — matches the Copay checkbox shape.
  // When on, the driver stop card renders a bright red "do NOT leave at
  // front door" banner so the flag can't be missed mid-route.
  const [mandatorySignature, setMandatorySignature] = useState(false);
  const [form, setForm] = useState({
    customer_name: initialPatientName || "",
    customer_phone: initialPatientPhone || "",
    address: initialAddress || "",
    pharmacy_id: defaultPharmacyId || (pharmacies[0]?.id ?? ""),
    priority: initialPriority || "normal",
    deliver_type: fixType(initialDeliverType || "same_day"),
    notes: initialNotes || "",
    order_number: "",
    copay: "",
  });
  const [items, setItems] = useState([{ name: "Regular Medication", qty: 1, notes: "" }]);
  // Marquez-only prescription capture: a chip input that accepts hand-typed
  // Rx numbers (Enter to commit) AND scanner-gun input (scanners typically
  // append Enter or Tab after each barcode, so the same handler works).
  // We keep a separate state from `items` so switching pharmacies doesn't
  // silently lose data — the arrays only merge in submit().
  const [rxNumbers, setRxNumbers] = useState([]);
  const [rxDraft, setRxDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Marquez scanner barcodes are 13 numeric digits; the meaningful Rx number
  // lives in positions 4-10 (1-indexed). Example: 4001354510036 → 1354510.
  // If the raw value is anything else (manual short entry, alphanumerics),
  // we keep it as-is so the operator's typed value isn't silently mangled.
  const formatRx = (raw) => {
    const t = String(raw).trim();
    return /^\d{13}$/.test(t) ? t.slice(3, 10) : t;
  };

  const selectedPharmacy = pharmacies.find((p) => p.id === form.pharmacy_id) || null;
  const manualNumbering = (selectedPharmacy?.order_numbering || "auto") === "manual";
  // Prescription-only mode: replaces the Items block with a Rx-number chip
  // input. Toggled by the pharmacy name containing "marquez" (case-insensitive)
  // — matches the operator's real-world naming.
  const prescriptionMode = !!(selectedPharmacy?.name || "").toLowerCase().includes("marquez");

  useEffect(() => {
    if (defaultPharmacyId) setForm((f) => ({ ...f, pharmacy_id: defaultPharmacyId }));
    else if (!form.pharmacy_id && pharmacies[0]) setForm((f) => ({ ...f, pharmacy_id: pharmacies[0].id }));
  }, [defaultPharmacyId, pharmacies]);

  // Allow the parent (e.g. PriceEstimator → "Use this address") to push values
  // into the form after mount.
  useEffect(() => {
    if (initialAddress) setForm((f) => ({ ...f, address: initialAddress }));
    if (initialDeliverType) setForm((f) => ({ ...f, deliver_type: fixType(initialDeliverType) }));
  }, [initialAddress, initialDeliverType]);

  useEffect(() => {
    if (!isStaff) return;
    const tick = () => {
      const passed = sameDayCutoffPassed();
      setCutoffActive(passed);
      if (passed) setForm((f) => (f.deliver_type === "same_day" ? { ...f, deliver_type: "next_day" } : f));
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [isStaff]);

  const onDeliverTypeChange = (v) => {
    if (v === "same_day" && isStaff && sameDayCutoffPassed()) {
      setCutoffActive(true);
      toast.warning(CUTOFF_MSG, { duration: 7000 });
      set("deliver_type", "next_day");
      return;
    }
    set("deliver_type", v);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (i, k, v) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addItem = () => setItems((a) => [...a, { name: "Regular Medication", qty: 1, notes: "" }]);
  const removeItem = (i) => setItems((a) => (a.length > 1 ? a.filter((_, idx) => idx !== i) : a));

  // Live restricted-ZIP list from the backend (Super Admin manages it on the
  // Audit Log tab). Fetched once at mount; failures degrade to an empty set
  // so a network hiccup never blocks order creation.
  const [restrictedZips, setRestrictedZips] = useState(new Set());
  useEffect(() => {
    let cancelled = false;
    api.get("/admin/restricted-zips")
      .then(({ data }) => { if (!cancelled) setRestrictedZips(new Set(data?.zips || [])); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    // Build the items payload:
    //  • Marquez (prescriptionMode) → combine BOTH the Rx-number chip input
    //    and the standard Items grid. Either or both may contribute rows —
    //    the pharmacy staff picks whichever fits the workflow.
    //  • Regular pharmacies → the usual editable Items grid only.
    let cleaned;
    if (prescriptionMode) {
      // Auto-commit any draft the operator forgot to press Enter on so a
      // half-typed Rx isn't silently dropped.
      const finalList = rxDraft.trim() ? [...rxNumbers, formatRx(rxDraft)] : rxNumbers;
      const rxItems = finalList.filter(Boolean).map((rx) => ({ name: `RX ${rx}`, qty: 1, notes: "" }));
      const gridItems = items.filter((it) => it.name.trim()).map((it) => ({ ...it, qty: Number(it.qty) || 1 }));
      cleaned = [...rxItems, ...gridItems];
      if (!cleaned.length) { toast.error("Add at least one prescription number or item"); return; }
    } else {
      cleaned = items.filter((it) => it.name.trim()).map((it) => ({ ...it, qty: Number(it.qty) || 1 }));
      if (!cleaned.length) { toast.error("Add at least one item"); return; }
    }
    if (manualNumbering && !form.order_number.trim()) {
      toast.error("This pharmacy uses manual order numbering — please enter an Order #.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, items: cleaned };
      if (!manualNumbering) delete payload.order_number;
      // Copay: only send when the role can set it, checkbox is on, and a value is provided
      if (canSetCopay && copayEnabled && form.copay !== "" && form.copay != null) {
        const num = Number(form.copay);
        if (Number.isFinite(num) && num >= 0) payload.copay = num;
        else delete payload.copay;
      } else {
        delete payload.copay;
      }
      payload.mandatory_signature = !!mandatorySignature;
      const { data } = await api.post("/orders", payload);
      if (data.restricted_zip) {
        toast.warning(
          `Order #${data.order_number || data.id.slice(0, 6)} placed On Hold — SRCI Admin approval required (restricted ZIP).`,
          { duration: 6000 }
        );
      } else {
        toast.success(`Order created for ${data.customer_name}${data.order_number ? ` · #${data.order_number}` : ""}`);
      }
      setForm({ customer_name: "", customer_phone: "", address: "", pharmacy_id: form.pharmacy_id, priority: "normal", deliver_type: fixType("same_day"), notes: "", order_number: "", copay: "" });
      setCopayEnabled(false);
      setMandatorySignature(false);
      setItems([{ name: "Regular Medication", qty: 1, notes: "" }]);
      setRxNumbers([]);
      setRxDraft("");
      onCreated?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="order-form">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="cust">Patient name</Label>
          <Input id="cust" required value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="order-customer-name" />
        </div>
        <div>
          <Label htmlFor="phone">Phone</Label>
          <Input id="phone" required value={form.customer_phone} onChange={(e) => set("customer_phone", formatPhoneInput(e.target.value))} placeholder="305-555-1234" data-testid="order-customer-phone" />
        </div>
      </div>
      <div>
        <Label htmlFor="addr">Delivery address</Label>
        <Input id="addr" required placeholder="123 Main St, City, State" value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="order-address" />
        <p className="text-xs text-muted-foreground mt-1">We&apos;ll auto-geocode this for routing.</p>
        {/* Restricted-ZIP live warning — matches the backend hold rule. */}
        {restrictedZips.has(extractZip(form.address) || "") && (
          <div
            className="mt-2 flex gap-2 items-start rounded-lg border border-[#f59e0b]/40 bg-[#fff7ed] text-[#7c2d12] px-3 py-2"
            data-testid="order-restricted-zip-alert"
          >
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="text-[12px] leading-relaxed">
              <strong>ZIP {extractZip(form.address)} is restricted.</strong> This order
              will be placed <strong>On Hold</strong> and requires <strong>SRCI Admin approval</strong> before
              a driver can pick it up.
            </div>
          </div>
        )}
      </div>

      {manualNumbering ? (
        <div data-testid="order-number-section">
          <Label htmlFor="onum">Order # <span className="text-destructive">*</span></Label>
          <Input
            id="onum"
            required
            placeholder="e.g. RX-2026-00045"
            value={form.order_number}
            onChange={(e) => set("order_number", e.target.value)}
            data-testid="order-number-input"
          />
          <p className="text-xs text-muted-foreground mt-1">
            {selectedPharmacy?.name} uses manual order numbering — enter the pharmacy's own Order # for this delivery.
          </p>
        </div>
      ) : (
        selectedPharmacy && (
          <p className="text-xs text-muted-foreground" data-testid="order-number-auto-hint">
            Order # will be generated automatically by the system for {selectedPharmacy.name}.
          </p>
        )
      )}

      <div className={`grid gap-4 ${allowPharmacySelect ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
        {allowPharmacySelect && (
          <div>
            <Label>Pharmacy</Label>
            <Select value={form.pharmacy_id} onValueChange={(v) => set("pharmacy_id", v)}>
              <SelectTrigger data-testid="order-pharmacy"><SelectValue placeholder="Select pharmacy" /></SelectTrigger>
              <SelectContent>
                {pharmacies.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label>Deliver type</Label>
          <Select value={form.deliver_type} onValueChange={onDeliverTypeChange}>
            <SelectTrigger data-testid="order-deliver-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="same_day">Same Day</SelectItem>
              <SelectItem value="next_day">Next Day</SelectItem>
              <SelectItem value="otc">OTC</SelectItem>
              <SelectItem value="pickup">Pick Up</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Priority</Label>
          <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
            <SelectTrigger data-testid="order-priority"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
              <SelectItem value="cold_chain">Cold Chain</SelectItem>
              <SelectItem value="clinic">Clinic</SelectItem>
              <SelectItem value="business">Business</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {cutoffActive && (
        <div
          className="flex gap-2 items-start rounded-lg border border-[#f59e0b]/40 bg-[#fff7ed] text-[#7c2d12] px-3 py-2"
          data-testid="order-same-day-cutoff-alert"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="text-[12px] leading-relaxed">
            <strong>Same Day cutoff time is 2:00 PM Eastern Time.</strong> Orders placed after 2:00 PM are
            automatically scheduled as <strong>Next Day</strong> delivery.
          </div>
        </div>
      )}

      {prescriptionMode && (
        <div data-testid="order-prescription-block">
          <div className="flex items-center justify-between mb-2">
            <Label htmlFor="rx-input">Prescription</Label>
            <span className="text-[11px] text-muted-foreground">
              {rxNumbers.length} added · Enter or scan to add
            </span>
          </div>
          {rxNumbers.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="order-rx-list">
              {rxNumbers.map((rx, i) => (
                <span
                  key={`${rx}-${i}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-2.5 py-1 text-[12px] font-mono-num"
                  data-testid={`order-rx-chip-${i}`}
                >
                  RX {rx}
                  <button
                    type="button"
                    onClick={() => setRxNumbers((a) => a.filter((_, idx) => idx !== i))}
                    className="opacity-60 hover:opacity-100"
                    aria-label={`Remove RX ${rx}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <Input
            id="rx-input"
            value={rxDraft}
            onChange={(e) => setRxDraft(e.target.value)}
            onKeyDown={(e) => {
              // Enter (manual) or Tab (some scanner guns) both commit the
              // current draft. Scanner guns that append Enter automatically
              // fire this handler once per barcode → multi-scan just works.
              if (e.key === "Enter" || e.key === "Tab") {
                if (rxDraft.trim()) {
                  e.preventDefault();
                  setRxNumbers((a) => [...a, formatRx(rxDraft)]);
                  setRxDraft("");
                }
              }
            }}
            placeholder="Type or scan a prescription number, then Enter"            autoComplete="off"
            data-testid="order-rx-input"
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            Works with barcode scanners — each shot is added as a chip automatically.
          </p>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Medicines / Items</Label>
          <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="order-add-item">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add item
          </Button>
        </div>
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2">
              <div className="col-span-7">
                <Select value={it.name} onValueChange={(v) => setItem(i, "name", v)}>
                  <SelectTrigger data-testid={`order-item-name-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{ITEM_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Input
                className="col-span-2"
                type="number"
                min={1}
                value={it.qty}
                onChange={(e) => setItem(i, "qty", e.target.value)}
                data-testid={`order-item-qty-${i}`}
              />
              <Input
                className="col-span-2"
                placeholder="notes"
                value={it.notes}
                onChange={(e) => setItem(i, "notes", e.target.value)}
              />
              <Button type="button" variant="ghost" size="icon" className="col-span-1" onClick={() => removeItem(i)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
        {prescriptionMode && (
          <p className="text-[11px] text-muted-foreground mt-1">
            Optional for Marquez — leave the default row if you only need prescription numbers above.
          </p>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-4 items-end">
        <div className="md:col-span-2">
          <Label htmlFor="notes">Delivery notes</Label>
          <Textarea id="notes" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Apartment buzzer, gate code, allergies..." data-testid="order-notes" />
        </div>
        <div className="space-y-3">
          {canSetCopay && (
            <div data-testid="order-copay-section">
              <Label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={copayEnabled}
                  onCheckedChange={(v) => {
                    const next = !!v;
                    setCopayEnabled(next);
                    if (!next) set("copay", "");
                  }}
                  data-testid="order-copay-toggle"
                />
                <span className="text-sm">Charge a copay</span>
              </Label>
              {copayEnabled && (
                <div className="relative mt-1.5" data-testid="order-copay-input-wrapper">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.copay}
                    onChange={(e) => set("copay", e.target.value)}
                    placeholder="0.00"
                    className="pl-7"
                    data-testid="order-copay"
                    autoFocus
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">Patient's out-of-pocket amount to collect on delivery.</p>
                </div>
              )}
            </div>
          )}
          {/* "Signature required" flag — visible to everyone who can create an
              order. Mirrors the Copay checkbox shape so the workflow feels
              familiar. When ON, the driver stop card shows a red
              "do NOT leave at front door" banner. */}
          <div data-testid="order-signature-section">
            <Label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={mandatorySignature}
                onCheckedChange={(v) => setMandatorySignature(!!v)}
                data-testid="order-signature-toggle"
              />
              <span className="text-sm">Mandatory Signature</span>
            </Label>
            {mandatorySignature && (
              <p className="text-[11px] text-destructive mt-1" data-testid="order-signature-hint">
                Driver will be alerted <strong>not to leave at the front door</strong> — the pharmacy needs the patient's signature.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            disabled={submitting}
            onClick={onCancel}
            className="rounded-full px-6"
            data-testid="order-cancel"
          >
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={submitting} className="rounded-full px-6" data-testid="order-submit">
          {submitting ? "Creating…" : "Create order"}
        </Button>
      </div>
    </form>
  );
}
